// Admin → Blog. List + create/edit form with the TipTap editor. Every
// write (save/delete/upload) goes through step-up 2FA (StepUpContext).

import { useRef, useState } from "react";

import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { useStepUp } from "@/context/StepUpContext";
import {
  uploadCoverImage,
  useAdminBlogList,
  useCreateBlogPost,
  useDeleteBlogPost,
  useUpdateBlogPost,
  type AdminBlogPost,
} from "@/hooks/useAdminBlog";
import { fmt, useDict } from "@/i18n/LocaleProvider";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";

type Locale = "pt" | "en" | "es";
type Status = "draft" | "published";
const LOCALE_NAMES: Record<Locale, string> = {
  pt: "Português",
  en: "English",
  es: "Español",
};

interface FormState {
  id?: string;
  title: string;
  slug: string;
  locale: Locale;
  excerpt: string;
  status: Status;
  coverUrl: string;
  bodyHtml: string;
}

function emptyForm(): FormState {
  return { title: "", slug: "", locale: "pt", excerpt: "", status: "draft", coverUrl: "", bodyHtml: "" };
}

export function AdminBlogTab() {
  const t = useDict().app.adminBlog;
  const { withStepUp } = useStepUp();
  const list = useAdminBlogList();
  const createMut = useCreateBlogPost();
  const updateMut = useUpdateBlogPost();
  const deleteMut = useDeleteBlogPost();

  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const coverInput = useRef<HTMLInputElement>(null);

  const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

  function save() {
    if (!form || !form.title.trim()) return;
    withStepUp(async (token) => {
      setBusy(true);
      const input = {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        locale: form.locale,
        excerpt: form.excerpt.trim() || undefined,
        cover_image_url: form.coverUrl || undefined,
        body_html: form.bodyHtml,
        status: form.status,
      };
      try {
        if (form.id) {
          await updateMut.mutateAsync({ id: form.id, input, stepUpToken: token });
        } else {
          await createMut.mutateAsync({ input, stepUpToken: token });
        }
        toast.success(t.saved);
        setForm(null);
      } catch (e) {
        toast.error(`${t.saveFailed} ${errMsg(e)}`);
      } finally {
        setBusy(false);
      }
    }, t.stepUpSave);
  }

  async function remove(post: AdminBlogPost) {
    const ok = await confirm({
      title: t.deleteTitle,
      message: fmt(t.deleteMsg, { title: post.title }),
      variant: "danger",
      confirmLabel: t.deleteConfirm,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        await deleteMut.mutateAsync({ id: post.id, stepUpToken: token });
        toast.success(t.deleted);
      } catch (e) {
        toast.error(errMsg(e));
      }
    }, t.stepUpDelete);
  }

  function uploadCover(file: File) {
    withStepUp(async (token) => {
      try {
        const url = await uploadCoverImage(file, token);
        setForm((f) => (f ? { ...f, coverUrl: url } : f));
      } catch {
        toast.error(t.uploadFailed);
      }
    }, t.stepUpUpload);
  }

  // Editor image button → returns the uploaded URL through the step-up flow.
  const onEditorImage = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      withStepUp(async (token) => {
        try {
          resolve(await uploadCoverImage(file, token));
        } catch (e) {
          toast.error(t.uploadFailed);
          reject(e);
        }
      }, t.stepUpUpload);
    });

  // ─── Form view ──────────────────────────────────────────────
  if (form) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="font-display text-[16px] font-semibold text-content">
            {form.id ? t.editPost : t.newPost}
          </div>
          <Button variant="ghost" small onClick={() => setForm(null)}>
            {t.cancel}
          </Button>
        </div>

        <Card>
          <Input label={t.fldTitle} value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Input
            label={t.fldSlug}
            value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })}
            placeholder={t.slugPlaceholder}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-mono text-[11px] text-muted uppercase tracking-[0.05em]">
                {t.fldLocale}
              </label>
              <select
                value={form.locale}
                onChange={(e) => setForm({ ...form, locale: e.target.value as Locale })}
                className="w-full px-3 py-2 min-h-[40px] bg-bg border border-stroke rounded text-content font-mono text-[13px]"
              >
                {(Object.keys(LOCALE_NAMES) as Locale[]).map((l) => (
                  <option key={l} value={l}>{LOCALE_NAMES[l]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-mono text-[11px] text-muted uppercase tracking-[0.05em]">
                {t.fldStatus}
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as Status })}
                className="w-full px-3 py-2 min-h-[40px] bg-bg border border-stroke rounded text-content font-mono text-[13px]"
              >
                <option value="draft">{t.draft}</option>
                <option value="published">{t.published}</option>
              </select>
            </div>
          </div>
          <Input
            label={t.fldExcerpt}
            value={form.excerpt}
            onChange={(v) => setForm({ ...form, excerpt: v })}
            placeholder={t.excerptPlaceholder}
            textarea
          />

          {/* Cover image */}
          <label className="block mb-1 font-mono text-[11px] text-muted uppercase tracking-[0.05em]">
            {t.fldCover}
          </label>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            {form.coverUrl && (
              <img src={form.coverUrl} alt="" className="h-16 w-auto rounded border border-stroke" />
            )}
            <Button variant="default" small onClick={() => coverInput.current?.click()}>
              {t.uploadCover}
            </Button>
            {form.coverUrl && (
              <Button variant="ghost" small onClick={() => setForm({ ...form, coverUrl: "" })}>
                {t.removeCover}
              </Button>
            )}
            <input
              ref={coverInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadCover(f);
                e.target.value = "";
              }}
            />
          </div>

          {/* Body */}
          <label className="block mb-1 font-mono text-[11px] text-muted uppercase tracking-[0.05em]">
            {t.fldBody}
          </label>
          <RichTextEditor
            key={form.id ?? "new"}
            value={form.bodyHtml}
            onChange={(html) => setForm((f) => (f ? { ...f, bodyHtml: html } : f))}
            onImageUpload={onEditorImage}
          />

          <div className="flex gap-2 mt-4">
            <Button variant="primary" onClick={save} disabled={busy || !form.title.trim()}>
              {busy ? t.saving : t.save}
            </Button>
            <Button variant="default" onClick={() => setForm(null)}>
              {t.cancel}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ─── List view ──────────────────────────────────────────────
  const posts = list.data ?? [];
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="font-mono text-[10px] text-muted">{posts.length}</div>
        <Button variant="primary" onClick={() => setForm(emptyForm())}>
          {t.newPost}
        </Button>
      </div>

      {list.isLoading ? (
        <Loading text={t.loading} />
      ) : posts.length === 0 ? (
        <Card>
          <div className="text-dim text-center py-6 font-mono text-[11px]">{t.noPosts}</div>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          {posts.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 px-4 py-3 border-b border-stroke font-mono text-[12px]"
            >
              <div className="flex-1 min-w-0">
                <div className="text-content font-semibold truncate">{p.title}</div>
                <div className="text-dim text-[10px] mt-[2px] truncate">/{p.slug}</div>
              </div>
              <span className="text-muted uppercase text-[9px] w-8">{p.locale}</span>
              <span
                className="px-2 py-[2px] rounded-[3px] text-[9px] font-bold uppercase"
                style={{
                  background: p.status === "published" ? "rgb(var(--color-success-bg))" : "rgb(var(--color-bg3))",
                  color: p.status === "published" ? "rgb(var(--color-success))" : "rgb(var(--color-muted))",
                }}
              >
                {p.status === "published" ? t.published : t.draft}
              </span>
              <Button variant="ghost" small onClick={() => setForm({
                id: p.id,
                title: p.title,
                slug: p.slug,
                locale: p.locale as Locale,
                excerpt: p.excerpt ?? "",
                status: p.status,
                coverUrl: p.cover_image_url ?? "",
                bodyHtml: p.body_html,
              })}>
                {t.edit}
              </Button>
              <Button
                variant="ghost"
                small
                onClick={() => remove(p)}
                className="!text-danger"
              >
                {t.delete}
              </Button>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
